import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Row } from '@kerfjs/ui/row';
import { Select } from '@kerfjs/ui/select';
import { FieldLabel, Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { canReturnDemand } from '../../../../shared/demand-transitions.js';
import { ListMarkdownPreview } from '../../../components/list-markdown-preview.js';
import { assignableMembers, descEditing, neededByEditing } from '../../../data/state.js';
import { canAssignDemands, canCreateDemands, isRequester } from '../../../lib/context.js';
import { formatDateOnly } from '../../../lib/format.js';

/**
 * The demand's responsible staff member (FR-5.20, HS2-C63T9N). Staff choose from the org's staff or
 * Unassigned, saved on change; anyone else (the requester) reads who is handling it. A current assignee
 * missing from the loaded staff list stays a choice so the control never shows a blank value.
 */
function AssigneeField({
  assigneeMemberId,
  assigneeName,
}: {
  assigneeMemberId: string | null;
  assigneeName: string | null;
}): SafeHtml {
  if (!canAssignDemands())
    return (
      <div role="group" aria-labelledby="assignee-label">
        <ListInsetControl sides="trl">
          <FieldLabel id="assignee-label">Assigned To</FieldLabel>
        </ListInsetControl>
        <ListInsetControl sides="rl">
          {assigneeName === null ? <Text tone="quiet">Unassigned</Text> : <Text>{assigneeName}</Text>}
        </ListInsetControl>
      </div>
    );
  const members = assignableMembers.value;
  const choices = [
    { value: '', label: 'Unassigned' },
    ...members.map((member) => ({ value: member.id, label: member.name })),
  ];
  if (assigneeMemberId !== null && !members.some((member) => member.id === assigneeMemberId))
    choices.push({ value: assigneeMemberId, label: assigneeName ?? 'Former member' });
  return (
    <ListInsetControl>
      <Select<string>
        name="demand-assignee"
        label="Assigned To"
        value={assigneeMemberId ?? ''}
        triggerWidth="fit-content"
        choices={choices}
      />
    </ListInsetControl>
  );
}

/** The Info segment (HS2-WRE7XP): Needed-by + the Markdown Description. Empty
 *  description edits on a single click; once it has content, double-click to edit
 *  (mirrors the ~/Documents/hotsheet2 markdown editor). */
export function InfoSegment({
  demand,
  readOnly,
}: {
  demand: {
    neededBy: string | null;
    purpose: string | null;
    status?: string;
    requesterMemberId?: string | null;
    assigneeMemberId?: string | null;
    assigneeName?: string | null;
  };
  readOnly: boolean;
}): SafeHtml {
  const desc = demand.purpose ?? '';
  const neededByIso = demand.neededBy != null ? demand.neededBy.slice(0, 10) : '';
  // Preview states are headed by Kerf's FieldLabel, which matches the themed Web
  // Awesome form-control label the edit state shows (HS2-HCAFSG, HS2-9DQ3W5); each
  // preview is a group named by its label.
  return (
    <List className="info-segment" gap="l" controlInsets="tb">
      <List>
        <AssigneeField assigneeMemberId={demand.assigneeMemberId ?? null} assigneeName={demand.assigneeName ?? null} />
      </List>
      {/* Needed by is click-to-edit like the title: a natural-width formatted-date
            preview by default, swapped for a compact date input while editing
            (HS2-8TG0P1). The input keeps its data-intake-field so blur auto-saves.
            Read-only on a cancelled demand — a plain preview (HS2-ZXQAM6). */}
      <List>
        {readOnly ? (
          <div role="group" aria-labelledby="needed-by-label">
            <ListInsetControl sides="trl">
              <FieldLabel id="needed-by-label">Needed By</FieldLabel>
            </ListInsetControl>
            <ListInsetControl sides="rl">
              {neededByIso === '' ? <Text tone="quiet">—</Text> : <Text>{formatDateOnly(neededByIso)}</Text>}
            </ListInsetControl>
          </div>
        ) : neededByEditing.value ? (
          <ListInsetControl>
            <TextField
              name="intake-neededBy"
              label="Needed By"
              type="date"
              value={neededByIso}
              rootAttributes={{ 'data-intake-field': 'neededBy', 'data-action': 'neededby-input' }}
            />
          </ListInsetControl>
        ) : (
          <div role="group" aria-labelledby="needed-by-label">
            <ListInsetControl sides="trl">
              <FieldLabel id="needed-by-label">Needed By</FieldLabel>
            </ListInsetControl>
            <ContentItem interactive action="edit-neededby" shape="rounded">
              <span>{neededByIso === '' ? 'Click to add a date' : formatDateOnly(neededByIso)}</span>
            </ContentItem>
          </div>
        )}
      </List>

      <List>
        {descEditing.value && !readOnly ? (
          <ListInsetControl>
            <TextField
              multiline
              name="intake-purpose"
              className="editor-purpose"
              label="Description"
              rows={4}
              placeholder="Edit markdown text"
              value={desc}
              rootAttributes={{ 'data-action': 'desc-input' }}
            />
          </ListInsetControl>
        ) : (
          <div role="group" aria-labelledby="description-label">
            <ListInsetControl sides="trl">
              <FieldLabel id="description-label">Description</FieldLabel>
            </ListInsetControl>
            <ListMarkdownPreview
              markdown={desc}
              emptyLabel={readOnly ? 'No description.' : 'Click to add markdown'}
              // A cancelled demand's description is read-only, empty or not (HS2-ZXQAM6).
              action={readOnly ? undefined : 'edit-desc'}
              emptyAction={readOnly ? undefined : 'edit-desc-empty'}
            />
          </div>
        )}
      </List>
      <Row controlInsets="rl" gap="xs" wrap>
        {!readOnly && !isRequester() && canReturnDemand(demand.status ?? '', demand.requesterMemberId != null) ? (
          <Button tone="neutral" emphasis="secondary" size="compact" action="demand-return-open">
            Return to Requester
          </Button>
        ) : null}
        {/* Any demand, in any state, can seed a new draft for a member who may create demands (HS2-Q2NPBT). */}
        {canCreateDemands() ? (
          <Button tone="neutral" emphasis="secondary" size="compact" action="demand-duplicate">
            Duplicate Demand
          </Button>
        ) : null}
        {!readOnly && !isRequester() ? (
          <Button emphasis="plain" size="compact" tone="danger" action="cancel-demand">
            Cancel Demand
          </Button>
        ) : null}
      </Row>
    </List>
  );
}
